/**
 * PlatformActionChip
 * A compact chip showing the platform label (APP/WEB) with wizard and workspace action buttons.
 * Uses blue (#277EA1) for APP and green (#2e7d32) for WEB.
 */

import React from 'react';
import {Box, Typography, IconButton, Tooltip, CircularProgress} from '@mui/material';
import {AutoFixHigh as WizardIcon} from '@mui/icons-material';

interface PlatformActionChipProps {
  platform: string;
  loading?: boolean;
  onWizardClick: () => void;
}

const PLATFORM_COLORS: Record<string, {accent: string; hover: string}> = {
  app: {accent: '#277EA1', hover: '#1f6a8a'},
  web: {accent: '#2e7d32', hover: '#256b29'},
};

const PlatformActionChip: React.FC<PlatformActionChipProps> = ({
  platform,
  loading = false,
  onWizardClick,
}) => {
  const colors = PLATFORM_COLORS[platform] ?? PLATFORM_COLORS.app;

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1,
        py: 0.5,
        borderRadius: 1.5,
        bgcolor: '#fafafa',
        border: '2px solid',
        borderColor: colors.accent,
      }}
    >
      <Typography
        variant="caption"
        sx={{fontWeight: 600, color: colors.accent}}
      >
        {platform.toUpperCase()}
      </Typography>

      {/* Wizard button */}
      <Tooltip title={`Wizard (${platform.toUpperCase()})`}>
        <IconButton
          size="small"
          disabled={loading}
          sx={{
            p: 0.3,
            bgcolor: colors.accent,
            color: 'white',
            '&:hover': {bgcolor: colors.hover},
          }}
          onClick={onWizardClick}
        >
          {loading ? (
            <CircularProgress size={16} sx={{color: 'white'}} />
          ) : (
            <WizardIcon sx={{fontSize: 16, color: 'white'}} />
          )}
        </IconButton>
      </Tooltip>

    </Box>
  );
};

export default PlatformActionChip;
